import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PAGE = {
  top: 18,
  right: 16,
  bottom: 16,
  left: 16,
};

const COLORS = {
  title: [17, 24, 39],
  subtitle: [100, 116, 139],
  section: [37, 99, 235],
  body: [31, 41, 55],
  muted: [75, 85, 99],
  success: [22, 163, 74],
  line: [226, 232, 240],
};

const toNumber = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const safeText = (text) => {
  if (text == null) return "";
  return String(text)
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/â†’|Ã¢â€ â€™|!Ã¢â‚¬â„¢/g, "->")
    .replace(/â€¢|Ã¢â‚¬Â¢/g, "-")
    .replace(/[â€“â€”]|Ã¢â‚¬â€œ|Ã¢â‚¬â€/g, "-")
    .replace(/[â€˜â€™]/g, "'")
    .replace(/[â€œâ€]/g, '"')
    .replace(/[^\x20-\x7E\n\r\t]/g, "")
    .replace(/[ \t]+/g, " ")
    .trim();
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\s*\/\s*(div|p|h[1-6]|ul|ol)\s*>/gi, "\n")
    .replace(/<\s*(div|p|h[1-6]|ul|ol)[^>]*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "\n- ")
    .replace(/<\s*\/\s*li\s*>/gi, "")
    .replace(/<\s*b[^>]*>|<\s*strong[^>]*>|<\s*mark[^>]*>/gi, "**")
    .replace(/<\s*\/\s*(b|strong|mark)\s*>/gi, "**")
    .replace(/<[^>]+>/g, "");

const parseBoldSegments = (line) => {
  const clean = String(line || "");
  const parts = clean.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part) => {
    const isBold = part.startsWith("**") && part.endsWith("**");
    return {
      bold: isBold,
      text: isBold ? part.slice(2, -2) : part,
    };
  });
};

const normalizeSummaryBlocks = (rawSummary) => {
  const text = stripHtml(rawSummary)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\s*(Module\s+\d+\s*:)/gi, "\n$1")
    .replace(/\n{3,}/g, "\n\n");

  return text
    .split("\n")
    .map((line) => safeText(line))
    .filter(Boolean)
    .map((line) => {
      const t = line.trim();
      if (/^module\s+\d+\s*:/i.test(t) || /^chapter\s+\d+/i.test(t) || /^batch\s+\d+/i.test(t)) {
        return { type: "heading", text: t };
      }
      if (/^-\s+/.test(t)) {
        return { type: "bullet", text: t.replace(/^-\s+/, "") };
      }
      return { type: "paragraph", text: t };
    });
};

const fileSafeName = (name) =>
  safeText(name || "Reviewer")
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80) || "Reviewer";

const addFooter = (doc) => {
  const pages = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.subtitle);
    doc.text(`Page ${i} of ${pages}`, pageWidth - PAGE.right, pageHeight - 7, { align: "right" });
  }
};

export const generatePDFDocument = ({ title, generatedData, config }) => {
  if (!generatedData) return;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE.left - PAGE.right;
  let y = PAGE.top;

  const ensureSpace = (needed = 8) => {
    if (y + needed <= pageHeight - PAGE.bottom) return;
    doc.addPage();
    y = PAGE.top;
  };

  const drawSectionTitle = (label) => {
    ensureSpace(14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...COLORS.section);
    doc.text(label, PAGE.left, y);
    y += 4;
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.4);
    doc.line(PAGE.left, y, pageWidth - PAGE.right, y);
    y += 6;
  };

  const drawWrapped = (text, opts = {}) => {
    const {
      fontSize = 11,
      color = COLORS.body,
      fontStyle = "normal",
      indent = 0,
      lineHeight = 5.4,
      gapAfter = 1.6,
      bullet = false,
    } = opts;

    const x = PAGE.left + indent;
    const width = contentWidth - indent;

    doc.setFont("helvetica", fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);

    const safe = safeText(text);
    if (!safe) return;

    const lines = doc.splitTextToSize(safe, width - (bullet ? 4 : 0));
    lines.forEach((line, idx) => {
      ensureSpace(lineHeight + 1);
      if (bullet && idx === 0) {
        doc.text("-", x, y);
      }
      doc.text(line, x + (bullet ? 4 : 0), y);
      y += lineHeight;
    });
    y += gapAfter;
  };

  const drawWrappedRich = (text, opts = {}) => {
    const {
      fontSize = 11,
      color = COLORS.body,
      fontStyle = "normal",
      indent = 0,
      lineHeight = 5.4,
      gapAfter = 1.6,
      bullet = false,
    } = opts;

    const x = PAGE.left + indent;
    const width = contentWidth - indent;
    const right = x + width;
    const startX = x + (bullet ? 4 : 0);
    const segments = parseBoldSegments(safeText(text));

    if (segments.length === 0) return;

    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    ensureSpace(lineHeight + 1);
    if (bullet) doc.text("-", x, y);

    let cursorX = startX;

    const moveToNextLine = () => {
      y += lineHeight;
      ensureSpace(lineHeight + 1);
      cursorX = startX;
    };

    const writeToken = (token, isBold) => {
      const raw = String(token || "");
      if (!raw) return;
      doc.setFont("helvetica", isBold ? "bold" : fontStyle);
      let piece = raw;
      let pieceWidth = doc.getTextWidth(piece);

      if (pieceWidth > width) {
        const chunks = doc.splitTextToSize(piece.trim(), width);
        chunks.forEach((chunk, idx) => {
          if (idx > 0 || cursorX > startX) moveToNextLine();
          doc.setFont("helvetica", isBold ? "bold" : fontStyle);
          doc.text(chunk, cursorX, y);
          cursorX += doc.getTextWidth(chunk);
        });
        return;
      }

      if (cursorX + pieceWidth > right && cursorX > startX) {
        moveToNextLine();
        piece = piece.trimStart();
        pieceWidth = doc.getTextWidth(piece);
      }

      if (!piece) return;
      doc.text(piece, cursorX, y);
      cursorX += pieceWidth;
    };

    segments.forEach((seg) => {
      const words = String(seg.text || "").split(/\s+/).filter(Boolean);
      words.forEach((word, idx) => {
        const token = cursorX === startX ? word : ` ${word}`;
        writeToken(token, seg.bold);
      });
    });

    y += lineHeight + gapAfter;
  };

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...COLORS.title);
  doc.text(fileSafeName(title), pageWidth / 2, y, { align: "center" });
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.subtitle);
  doc.text(`Generated by ReviewMate • ${new Date().toLocaleString()}`, pageWidth / 2, y, {
    align: "center",
  });
  y += 8;

  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.5);
  doc.line(PAGE.left, y, pageWidth - PAGE.right, y);
  y += 8;

  // Summary
  if (generatedData.summary && config?.summaryEnabled) {
    drawSectionTitle("Summary");
    const blocks = normalizeSummaryBlocks(generatedData.summary);

    blocks.forEach((block) => {
      if (block.type === "heading") {
        drawWrappedRich(block.text, {
          fontSize: 12.5,
          color: COLORS.title,
          fontStyle: "bold",
          lineHeight: 6,
          gapAfter: 2.8,
        });
      } else if (block.type === "bullet") {
        drawWrappedRich(block.text, {
          fontSize: 11,
          color: COLORS.body,
          bullet: true,
          indent: 1,
          lineHeight: 5.3,
          gapAfter: 1.2,
        });
      } else {
        drawWrappedRich(block.text, {
          fontSize: 11,
          color: COLORS.body,
          lineHeight: 5.3,
          gapAfter: 1.8,
        });
      }
    });

    y += 3;
  }

  // Flashcards
  if (Array.isArray(generatedData.flashcards) && generatedData.flashcards.length > 0 && config?.flashcardsEnabled) {
    drawSectionTitle("Flashcards");

    autoTable(doc, {
      startY: y,
      head: [["Concept", "Definition"]],
      body: generatedData.flashcards.map((f) => [safeText(f?.front), safeText(f?.back)]),
      theme: "grid",
      tableWidth: contentWidth,
      margin: { left: PAGE.left, right: PAGE.right },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "left",
      },
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: 2.5,
        textColor: [31, 41, 55],
        overflow: "linebreak",
        valign: "top",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 56 },
        1: { cellWidth: contentWidth - 56 },
      },
    });

    y = (doc.lastAutoTable?.finalY || y) + 8;
  }

  // Quiz
  if (Array.isArray(generatedData.quiz) && generatedData.quiz.length > 0 && config?.quizEnabled) {
    drawSectionTitle("Practice Quiz");

    const secs = toNumber(config?.timePerQuestion, 30);
    drawWrapped(`Time allowed: ${secs}s per question`, {
      fontSize: 10,
      color: COLORS.subtitle,
      lineHeight: 4.8,
      gapAfter: 4,
    });

    generatedData.quiz.forEach((q, i) => {
      const qText = `${i + 1}. ${safeText(q?.question)}`;
      drawWrapped(qText, {
        fontSize: 11.2,
        fontStyle: "bold",
        color: COLORS.title,
        lineHeight: 5.6,
        gapAfter: 2.4,
      });

      const options = Array.isArray(q?.options) ? q.options : [];
      if (options.length > 0) {
        options.forEach((opt, idx) => {
          drawWrapped(`${String.fromCharCode(65 + idx)}. ${safeText(opt)}`, {
            fontSize: 10.6,
            color: COLORS.body,
            indent: 3,
            lineHeight: 5.1,
            gapAfter: 1,
          });
        });
      }

      drawWrapped(`Answer: ${safeText(q?.answer)}`, {
        fontSize: 10.5,
        fontStyle: "bold",
        color: COLORS.success,
        indent: 3,
        lineHeight: 5.1,
        gapAfter: 1.2,
      });

      if (q?.explanation) {
        drawWrapped(`Explanation: ${safeText(q.explanation)}`, {
          fontSize: 10.2,
          color: COLORS.muted,
          indent: 3,
          lineHeight: 5.1,
          gapAfter: 2.6,
        });
      } else {
        y += 1.6;
      }

      ensureSpace(6);
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.2);
      doc.line(PAGE.left + 2, y, pageWidth - PAGE.right - 2, y);
      y += 4;
    });
  }

  addFooter(doc);

  const fileName = `${fileSafeName(title || "Reviewer")}.pdf`;
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (isIOS) {
    window.open(doc.output("bloburl"), "_blank");
  } else {
    doc.save(fileName);
  }
};
